/**
 * NotificationItem — single notification card with avatar, type badge,
 * title/message/time, and action buttons for buddy requests and group joins.
 * The avatar opens the actor's profile; the title carries the row's own action
 * so the card needs no ARIA button role around nested controls.
 */
import { useMemo, type ComponentType } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BellIcon, BuddiesIcon, CommentIcon, HeartIcon, UserIcon } from '../../components/ui/icons';
import type { IconProps } from '../../components/ui/icons';
import type { NotificationItem as NotificationItemType } from '@wlb/shared/types/notification';
import { avatarFallback } from '../../components/ui/avatarFallback';
import { useRelativeTime } from '../../utils/useRelativeTime';
import '../../components/ui/avatar.css';
import './notifications.css';

interface BadgeConfig {
  bg: string;
  Icon: ComponentType<IconProps>;
}

interface NotificationItemProps {
  notification: NotificationItemType;
  onPress: (notification: NotificationItemType) => void;
  onAccept: (id: string, senderId?: number) => void;
  onReject: (id: string, senderId?: number) => void;
  onJoin: (id: string) => void;
  onView: (notification: NotificationItemType) => void;
  onOpenProfile: (notification: NotificationItemType) => void;
}

export function NotificationItem({
  notification,
  onPress,
  onAccept,
  onReject,
  onJoin,
  onView,
  onOpenProfile,
}: NotificationItemProps) {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const { type, title, message, isRead, user, status, id, senderId } = notification;
  // Same formatter the feed and comments use, so a five-minute-old like and a
  // five-minute-old post read the same.
  const time = relativeTime(notification.createdAt, {
    fallback: notification.time,
    naiveZone: 'local',
  });

  const badge = useMemo<BadgeConfig>(() => {
    switch (type) {
      case 'buddyRequest':
        return { bg: 'var(--color-secondary)', Icon: UserIcon };
      case 'like':
        return { bg: 'var(--color-error)', Icon: HeartIcon };
      case 'comment':
        return { bg: 'var(--color-success)', Icon: CommentIcon };
      case 'groupJoinRequest':
      case 'groupPost':
        return { bg: 'var(--color-accent)', Icon: BuddiesIcon };
      default:
        return { bg: 'var(--color-primary)', Icon: BellIcon };
    }
  }, [type]);

  const isActioned = Boolean(status);
  const avatarUrl = typeof user?.avatar === 'object' ? user.avatar?.uri : user?.avatar;
  // The title is a sentence, not a name, so it only ever supplies a letter.
  const avatarSender = avatarFallback({ seed: senderId ?? user?.name, name: user?.name || title });

  // Only rows carrying the actor's GUID can resolve a profile route.
  const canOpenProfile = Boolean(notification.actorUserGuid);
  const showBuddyActions = type === 'buddyRequest' && !status;
  const showGroupActions = type === 'groupJoinRequest' && !status;
  const cardClickable = !showBuddyActions && !showGroupActions;

  const avatarVisual = avatarUrl ? (
    <img src={avatarUrl} className="notification-item__avatar" alt="" aria-hidden />
  ) : (
    <span
      className="notification-item__avatar-fallback"
      data-avatar-tone={avatarSender.tone}
      aria-hidden
    >
      {avatarSender.initial}
    </span>
  );

  return (
    <article
      className={[
        'notification-item',
        !isRead && !isActioned ? 'notification-item--unread' : '',
        cardClickable ? 'notification-item--clickable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={cardClickable ? () => onPress(notification) : undefined}
    >
      <div className="notification-item__header">
        <span className="notification-item__avatar-wrap">
          {canOpenProfile ? (
            <button
              type="button"
              className="notification-item__avatar-button"
              aria-label={t('home.accessibility.openProfile')}
              onClick={event => {
                event.stopPropagation();
                onOpenProfile(notification);
              }}
            >
              {avatarVisual}
            </button>
          ) : (
            avatarVisual
          )}
          <span className="notification-item__badge" style={{ background: badge.bg }} aria-hidden>
            <badge.Icon size={11} />
          </span>
        </span>

        <div className="notification-item__content">
          {cardClickable ? (
            <button
              type="button"
              className="notification-item__title notification-item__title-button"
              aria-label={`${title}. ${message}. ${time}`}
              onClick={event => {
                event.stopPropagation();
                onPress(notification);
              }}
            >
              {title}
            </button>
          ) : (
            <p className="notification-item__title">{title}</p>
          )}
          <p className="notification-item__message">{message}</p>
          <p className="notification-item__time">{time}</p>
        </div>
      </div>

      {showBuddyActions && (
        <div className="notification-item__actions">
          <button
            type="button"
            className="notification-item__action notification-item__action--primary"
            onClick={event => { event.stopPropagation(); onAccept(id, senderId); }}
          >
            {t('home.notifications.buttons.accept')}
          </button>
          <button
            type="button"
            className="notification-item__action notification-item__action--secondary"
            onClick={event => { event.stopPropagation(); onReject(id, senderId); }}
          >
            {t('home.notifications.buttons.reject')}
          </button>
        </div>
      )}

      {showGroupActions && (
        <div className="notification-item__actions">
          <button
            type="button"
            className="notification-item__action notification-item__action--primary"
            onClick={event => { event.stopPropagation(); onJoin(id); }}
          >
            {t('home.notifications.buttons.join')}
          </button>
          <button
            type="button"
            className="notification-item__action notification-item__action--secondary"
            onClick={event => { event.stopPropagation(); onView(notification); }}
          >
            {t('home.notifications.buttons.view')}
          </button>
        </div>
      )}
    </article>
  );
}
